// SQLite tree-sitter parser loader — lazy, module-level singleton. PRD §3.1
// GG3: only loaded when a connection whose dialect is sqlite first parses SQL;
// never loaded on Oracle/MSSQL/PG/MySQL connections. Mirrors the Slice 1.5 /
// 2.5 / 3.5 / 4.5 parser.ts shape — same loader-injection seam, same
// reset-for-test affordance.

import { Language, Parser } from 'web-tree-sitter';

import { loadGrammarWasmBytes } from '../shared/wasmLoader';

export type SqliteWasmLoader = () => Promise<Uint8Array>;

let loader: SqliteWasmLoader | null = null;
let parserPromise: Promise<Parser> | null = null;

export function setSqliteWasmLoader(next: SqliteWasmLoader | null): void {
  loader = next;
  parserPromise = null;
}

function defaultLoader(): Promise<Uint8Array> {
  return loadGrammarWasmBytes(
    'sqlite',
    import.meta.url,
    () => import('../../../../../resources/tree-sitter/sqlite.wasm?url'),
  );
}

async function initParser(): Promise<Parser> {
  await Parser.init();
  const wasmBytes = await (loader ?? defaultLoader)();
  const language = await Language.load(wasmBytes);
  const parser = new Parser();
  parser.setLanguage(language);
  return parser;
}

export function getSqliteParser(): Promise<Parser> {
  if (!parserPromise) {
    // A FAILED load (missing wasm, a corrupted download) must not be cached
    // forever: clear the slot so the next call retries instead of replaying
    // the same rejection for the life of the process. The rejection itself
    // still reaches every caller of this attempt.
    const attempt = initParser();
    parserPromise = attempt;
    attempt.catch(() => {
      if (parserPromise === attempt) parserPromise = null;
    });
  }
  return parserPromise;
}

export function __resetSqliteParserForTest(): void {
  parserPromise = null;
}
